import type { OwnedGLBInstance } from './types'

interface PendingDisposal {
    timeout:ReturnType<typeof setTimeout>
}

const GLB_DISPOSAL_GRACE_MS = 100

const pendingDisposals = new WeakMap<
    OwnedGLBInstance,
    PendingDisposal
>()

/**
 * Cancel a disposal scheduled by React's development StrictMode effect
 * replay. Each owned GLB belongs to one hook mount, so retaining means the
 * same effect has immediately been set up again.
 */
export const retainOwnedGLBInstance = (
    instance:OwnedGLBInstance,
):void => {
    const pendingDisposal = pendingDisposals.get(instance)
    if(pendingDisposal) {
        clearTimeout(pendingDisposal.timeout)
    }
    pendingDisposals.delete(instance)
}

/**
 * Dispose after the current effect lifecycle settles.
 *
 * React StrictMode intentionally performs setup -> cleanup -> setup when a
 * component mounts in development. Immediate cleanup would permanently
 * dispose the instance reused by the second setup. A real unmount or URL
 * change has no matching retain, so its resources are still released after a
 * short grace period. React Three Fiber uses a nested root whose replay can
 * cross a task boundary, hence this cannot be only a microtask defer.
 */
export const releaseOwnedGLBInstance = (
    instance:OwnedGLBInstance,
):void => {
    const existingDisposal = pendingDisposals.get(instance)
    if(existingDisposal) {
        clearTimeout(existingDisposal.timeout)
    }

    const pendingDisposal:PendingDisposal = {
        timeout: setTimeout(() => {
            if(pendingDisposals.get(instance) !== pendingDisposal) return

            pendingDisposals.delete(instance)
            instance.dispose()
        }, GLB_DISPOSAL_GRACE_MS),
    }
    pendingDisposals.set(instance, pendingDisposal)
}
